/**
 * Keeps metafield-driven variant galleries in sync with the custom product
 * variant dropdown. Native Horizon variant pickers already re-render their
 * product section, so this bridge only handles the AI-generated dropdown.
 */

const state = (window.__ilashVariantGallerySync ??= {
  bound: false,
  controllers: new Map(),
});

if (!state.bound) {
  state.bound = true;
  document.addEventListener('change', handleVariantChange);
}

/**
 * @param {Event} event
 */
async function handleVariantChange(event) {
  const select = event.target;

  if (!(select instanceof HTMLSelectElement) || !select.matches('product-variant-dropdown [data-hidden-select]')) {
    return;
  }

  const dropdown = select.closest('product-variant-dropdown');
  const variantId = select.value;
  const sectionId = dropdown?.dataset.sectionId;

  if (!variantId || !sectionId) return;

  const section = document.getElementById(`shopify-section-${sectionId}`);
  const currentGallery = section?.querySelector('media-gallery[data-variant-gallery-enabled="true"]');

  // Products without any variant gallery metafields retain the dropdown's
  // existing local featured-image switching and make no network request.
  if (!currentGallery) return;

  state.controllers.get(sectionId)?.abort();

  const controller = new AbortController();
  state.controllers.set(sectionId, controller);
  currentGallery.setAttribute('aria-busy', 'true');

  const requestUrl = new URL(window.location.href);
  requestUrl.searchParams.set('variant', variantId);
  requestUrl.searchParams.set('section_id', sectionId);

  try {
    const response = await fetch(requestUrl.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Section request failed with status ${response.status}`);
    }

    const responseHtml = await response.text();

    // Ignore a response if the customer selected a newer variant while it was loading.
    if (state.controllers.get(sectionId) !== controller) return;

    const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
    const renderedGallery = responseDocument.querySelector('media-gallery');
    const liveGallery = document
      .getElementById(`shopify-section-${sectionId}`)
      ?.querySelector('media-gallery[data-variant-gallery-enabled="true"]');

    if (!renderedGallery || !liveGallery) {
      throw new Error(`Gallery not found in rendered section ${sectionId}`);
    }

    liveGallery.replaceWith(renderedGallery);
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      console.error('Variant gallery refresh failed:', error);
    }
  } finally {
    if (state.controllers.get(sectionId) === controller) {
      state.controllers.delete(sectionId);
      document
        .getElementById(`shopify-section-${sectionId}`)
        ?.querySelector('media-gallery')
        ?.removeAttribute('aria-busy');
    }
  }
}
